import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  Snackbar,
  Alert
} from '@mui/material';
import { ContentCopy as ContentCopyIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useFiles } from '../hooks/useFilesContext.js';

const XANO_BASE_URL = 'https://xlxn-yfz8-5hn7.n7d.xano.io';

function ImagePreviewModal({ open, onClose, file }) {
  const [copySuccess, setCopySuccess] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { deleteFile } = useFiles();

  if (!file) return null;

  const imageUrl = `${XANO_BASE_URL}${file.path}`;
  const fileSize = (file.size / 1024 / 1024).toFixed(2);
  
  // Acessando as dimensões do campo metadata
  let dimensions = { width: 'N/A', height: 'N/A' };
  try {
    if (file.metadata) {
      const metadata = typeof file.metadata === 'string' ? JSON.parse(file.metadata) : file.metadata;
      dimensions = {
        width: metadata.width || 'N/A',
        height: metadata.height || 'N/A'
      };
    }
  } catch (error) {
    console.error('Erro ao processar metadata:', error);
  }

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(imageUrl);
      setCopySuccess(true);
    } catch (err) {
      console.error('Erro ao copiar URL:', err);
    }
  };

  const handleCloseSnackbar = () => {
    setCopySuccess(false);
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteFile(file.id);
      setShowDeleteConfirm(false);
      onClose();
    } catch (error) {
      console.error('Erro ao excluir arquivo:', error);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {file.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              component="img"
              src={imageUrl}
              alt={file.name}
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: '60vh',
                objectFit: 'contain'
              }}
            />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Informações da Imagem
              </Typography>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">Dimensões:</Typography>
                <Typography variant="body2">{`${dimensions.width} x ${dimensions.height} pixels`}</Typography>
                
                <Typography variant="body2" color="text.secondary">Tamanho:</Typography>
                <Typography variant="body2">{`${fileSize} MB`}</Typography>
                
                <Typography variant="body2" color="text.secondary">Formato:</Typography>
                <Typography variant="body2">{file.type}</Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                URL da Imagem
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  value={imageUrl}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    readOnly: true,
                  }}
                />
                <IconButton
                  onClick={handleCopyUrl}
                  color="primary"
                  sx={{ flexShrink: 0 }}
                >
                  <ContentCopyIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteClick}
            color="error"
            startIcon={<DeleteIcon />}
          >
            Excluir
          </Button>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmação de exclusão */}
      <Dialog
        open={showDeleteConfirm}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir a imagem "{file.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancelar</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          URL copiada para a área de transferência!
        </Alert>
      </Snackbar>
    </>
  );
}

export default ImagePreviewModal; 